import { normalizeCountry } from "./country"
import { SEASONAL_THEMES } from "./themes"
import type { MonthDay, SeasonalTheme, SeasonalWindow } from "./types"

const formatters = new Map<string, Intl.DateTimeFormat>()

function formatterFor(timeZone: string): Intl.DateTimeFormat {
  const cached = formatters.get(timeZone)
  if (cached) return cached
  const created = new Intl.DateTimeFormat("en-US", { timeZone, month: "numeric", day: "numeric" })
  formatters.set(timeZone, created)
  return created
}

/** The calendar month and day of `now` in `timeZone`. */
export function calendarDayIn(now: Date, timeZone: string): MonthDay {
  const parts = formatterFor(timeZone).formatToParts(now)
  const read = (type: "month" | "day") => Number(parts.find((part) => part.type === type)?.value)
  return { month: read("month"), day: read("day") }
}

function ordinal({ month, day }: MonthDay): number {
  return month * 100 + day
}

/** Whether `date` falls inside `window`, ends included. */
export function isWithinWindow(window: SeasonalWindow, date: MonthDay): boolean {
  switch (window.kind) {
    case "month":
      return date.month === window.month
    case "day":
      return date.month === window.month && date.day === window.day
    case "range": {
      const value = ordinal(date)
      const start = ordinal(window.start)
      const end = ordinal(window.end)
      return start <= end ? value >= start && value <= end : value >= start || value <= end
    }
  }
}

function appliesTo(theme: SeasonalTheme, country: string | undefined): boolean {
  if (theme.regions === "global") return true
  const code = normalizeCountry(country)
  return code !== null && theme.regions.includes(code)
}

/** Input of `resolveSeasonalTheme`: the instant, the merchant zone and its raw country. */
export type ResolveSeasonalThemeInput = {
  now: Date
  timeZone: string
  country?: string | null
  themes?: readonly SeasonalTheme[]
}

/** The highest-priority theme active on the merchant's calendar day and region, or null. */
export function resolveSeasonalTheme({ now, timeZone, country, themes = SEASONAL_THEMES }: ResolveSeasonalThemeInput): SeasonalTheme | null {
  const date = calendarDayIn(now, timeZone)
  let best: SeasonalTheme | null = null
  for (const theme of themes) {
    if (!isWithinWindow(theme.window, date) || !appliesTo(theme, country ?? undefined)) continue
    if (!best || theme.priority > best.priority) best = theme
  }
  return best
}
